import { Button, Spinner, Text } from "../uikit";
import React, { useEffect, useState } from "react";
import "../index.css";
import SettingsIcon from "../img/settings.svg";
import ChromeIcon from "../img/chrome.svg";
import FirefoxIcon from "../img/firefox.svg";
import TermsIcon from "../img/terms.svg";
import Card from "../components/Card";
import { routeVariants } from "../routesVariants";

import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router";
import SupportButton from "../components/SupportButton";

import { useTranslation } from "react-i18next";
import ErrorMessage from "../components/ErrorMessage";
import SubscriptionCard from "../components/SubscriptionCard";
import { DOMAIN } from "../consts";
import { getDemoAccess, getUserData } from "../utils/Api";
import { getToken, clearToken } from "../utils/tokenStorage";
import { checkCompletedPayment } from "../utils/paymentTracking";

export default function Account() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isGettingDemo, setIsGettingDemo] = useState(false);
  const [subs, setSubs] = useState([]);
  const [onlineByLogin, setOnlineByLogin] = useState({});
  const [isOnlineLoading, setIsOnlineLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadOnline = async (token) => {
    setIsOnlineLoading(true);
    try {
      const response = await fetch(DOMAIN + "/subscriptions/online", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((r) => r.json());

      if (!response.success) {
        return;
      }

      const counts = {};
      for (const connection of response.connections || []) {
        const login = connection.login;
        if (!login) {
          continue;
        }
        counts[login] = (counts[login] || 0) + 1;
      }
      setOnlineByLogin(counts);
    } catch {
    } finally {
      setIsOnlineLoading(false);
    }
  };

  const getData = async () => {
    try {
      setIsLoading(true);

      const [success, result, status] = await getUserData();

      if (success) {
        setSubs(result.subs);
        const token = getToken();
        if (token) {
          await loadOnline(token);
        } else {
          setIsOnlineLoading(false);
        }
        if (localStorage.getItem("lang") !== result.lang) {
          // Язык из профиля применяем на живую — перезагрузка страницы
          // давала двойную загрузку при каждом открытии мини-аппа.
          localStorage.setItem("lang", result.lang);
          i18n.changeLanguage(result.lang);
        }
      } else if (status === 401) {
        clearToken();
        navigate("/login", { replace: true });
      } else {
        setErrorMessage("Something went wrong");
        setIsOnlineLoading(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // Проверка отложенных платежей в фоне: ничего не блокирует
    checkCompletedPayment();
  }, []);

  const getDemo = async () => {
    try {
      setIsGettingDemo(true);
      setErrorMessage("");

      const result = await getDemoAccess();
      if (result.isUnauthorized) {
        clearToken();
        navigate("/login");
        return;
      }

      if (result.success) {
        await getData();
      } else {
        setErrorMessage(result.message || "Something went wrong");
      }
    } finally {
      setIsGettingDemo(false);
    }
  };

  return (
    <div className="background-regular full-height">
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={isLoading}
          className="container column-spacing border-padding background-regular column"
          variants={routeVariants}
          initial="initial"
          animate="final"
          exit="exit"
          style={{ position: "absolute" }}
        >
          {isLoading ? (
            <div className="center full-height">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="row header">
                <Text weight="2">{t("Subscriptions")}</Text>
                <Button
                  mode="plain"
                  size="s"
                  onClick={() => navigate("/account/subscription")}
                >
                  {t("Add new")}
                </Button>
              </div>
              {subs.length ? (
                subs.map((row) => (
                  <SubscriptionCard
                    key={row.id}
                    row={row}
                    onlineCount={onlineByLogin[row.login] || 0}
                    isOnlineCountLoading={isOnlineLoading}
                  />
                ))
              ) : (
                <Card>
                  <div className="column card">
                    <div className="row center">
                      <Text weight="3">
                        {t("You don't have any active subscriptions")}
                      </Text>
                    </div>
                    <Button
                      className="row"
                      mode="filled"
                      size="l"
                      onClick={() => navigate("/account/subscription")}
                    >
                      {t("Add a new subscription")}
                    </Button>
                    <Button
                      className="row"
                      mode="outline"
                      size="l"
                      loading={isGettingDemo}
                      disabled={isGettingDemo}
                      onClick={() => getDemo()}
                    >
                      {t("Get demo access")}
                    </Button>
                    <ErrorMessage errorMessage={errorMessage} />
                  </div>
                </Card>
              )}
              <SupportButton />
              <Button
                mode="gray"
                size="l"
                before={
                  <img src={SettingsIcon} alt="" className="monochrome-icon" />
                }
                onClick={() => navigate("/account/settings")}
              >
                {t("Account settings")}
              </Button>
              <Button
                mode="gray"
                size="l"
                before={
                  <img src={TermsIcon} alt="" className="monochrome-icon" />
                }
                onClick={() => navigate("/termsandpolicies")}
              >
                {t("The terms and policies")}
              </Button>
              {/*<Button
                mode="gray"
                size="l"
                before={<img src={ChromeIcon} alt="" />}
              >
                {t("Chrome extension")}
              </Button>
              <Button
                mode="gray"
                size="l"
                before={<img src={FirefoxIcon} alt="" />}
              >
                {t("Firefox extension")}
              </Button>*/}
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
